import type {
    CommerceEnrollmentSession,
    CommerceEnrollmentStage,
    CommerceMoney,
    CommerceProductSummary,
} from "@teqtank/commerce-contracts";
import { CommerceTransportError, MockCommerceTransport } from "@teqtank/commerce-transport";
import { beginCommerceEnrollmentOperation } from "../operations/begin-commerce-enrollment-operation.js";
import { getCommerceEnrollmentOperation } from "../operations/get-commerce-enrollment-operation.js";
import { selectCommerceEnrollmentKitOperation } from "../operations/select-commerce-enrollment-kit-operation.js";
import { completeCommerceEnrollmentStageOperation } from "../operations/complete-commerce-enrollment-stage-operation.js";
import { abandonCommerceEnrollmentOperation } from "../operations/abandon-commerce-enrollment-operation.js";
import { addCommerceEnrollmentProductOperation } from "../operations/add-commerce-enrollment-product-operation.js";
import { returnCommerceEnrollmentToShoppingOperation } from "../operations/return-commerce-enrollment-to-shopping-operation.js";
import type { DefaultCartController } from "../cart/register-default-cart-operations.js";

export function registerDefaultEnrollmentOperations(
    transport: MockCommerceTransport,
    cart: DefaultCartController,
): () => CommerceEnrollmentSession | undefined {
    const sessions = new Map<string, CommerceEnrollmentSession>();
    transport.register(beginCommerceEnrollmentOperation, () => {
        cart.replace([], 0);
        const now = new Date();
        const stages = defaultStages();
        const enrollment: CommerceEnrollmentSession = {
            id: `enrollment:${Date.now()}`,
            version: 1,
            status: "active",
            marketId: "NZ",
            eligibility: { eligible: true, code: null, message: null },
            currentStage: stages[0]!,
            stages,
            categories: [],
            kits: [
                kit("starter", "Essential Starter", 149),
                kit("builder", "Business Builder", 299),
            ],
            selectedKitId: null,
            cart: emptyCart(),
            createdAt: now,
            updatedAt: now,
        };
        sessions.set(enrollment.id, enrollment);
        return { enrollment };
    });
    transport.register(getCommerceEnrollmentOperation, ({ enrollmentId }) => ({
        enrollment: enrollmentId
            ? (sessions.get(enrollmentId) ?? null)
            : ([...sessions.values()].at(-1) ?? null),
    }));
    transport.register(
        selectCommerceEnrollmentKitOperation,
        ({ enrollmentId, kitId, expectedVersion }) => {
            const current = requireJourney(sessions, enrollmentId, expectedVersion);
            const selected = current.kits.find((value) => value.id === kitId);
            if (!selected) throw failed(selectCommerceEnrollmentKitOperation.name);
            const money = selected.product.representativeOffers[0]!.price.current;
            const line = {
                id: `${kitId}:default:one-time`,
                productId: selected.product.id,
                variantId: `${kitId}:default`,
                ...(selected.product.displaySku ? { displaySku: selected.product.displaySku } : {}),
                name: selected.product.name,
                image: selected.product.primaryImage,
                purchaseType: "one-time" as const,
                quantity: 1,
                unitPrice: money,
                lineSubtotal: money,
                lineDiscount: amount(0),
                lineTotal: money,
            };
            const enrollment = {
                ...current,
                version: current.version + 1,
                selectedKitId: kitId,
                currentStage: current.stages[1]!,
                cart: {
                    ...current.cart,
                    version: current.cart.version + 1,
                    lines: [
                        {
                            line,
                            role: "kit" as const,
                            quantityLocked: true,
                            removalLocked: true,
                            lockReason: "Included with the selected enrollment collection.",
                        },
                    ],
                    totals: {
                        itemCount: 1,
                        subtotal: money,
                        discount: amount(0),
                        shipping: amount(0),
                        tax: amount(0),
                        total: money,
                    },
                },
                updatedAt: new Date(),
            };
            sessions.set(enrollment.id, enrollment);
            cart.replace(
                enrollment.cart.lines.map((value) => value.line),
                enrollment.cart.version,
            );
            return { enrollment };
        },
    );
    transport.register(
        addCommerceEnrollmentProductOperation,
        ({
            enrollmentId,
            productId,
            variantId,
            quantity,
            purchaseType,
            frequencyDays,
            expectedVersion,
        }) => {
            const current = requireJourney(sessions, enrollmentId, expectedVersion);
            const money = amount(purchaseType === "recurring" ? 36 : 42);
            const id = `${productId}:${variantId ?? "default"}:${purchaseType}`;
            const line = {
                id,
                productId,
                variantId: variantId ?? `${productId}:default`,
                name: "Enrollment product",
                image: null,
                purchaseType,
                ...(frequencyDays === undefined ? {} : { frequencyDays }),
                quantity,
                unitPrice: money,
                lineSubtotal: amount(money.amount * quantity),
                lineDiscount: amount(0),
                lineTotal: amount(money.amount * quantity),
            };
            const retained = current.cart.lines.filter((value) => value.line.id !== id);
            const lines = [
                ...retained,
                {
                    line,
                    role: "optional" as const,
                    quantityLocked: false,
                    removalLocked: false,
                    lockReason: null,
                },
            ];
            const subtotal = lines.reduce((sum, value) => sum + value.line.lineTotal.amount, 0);
            const enrollment = {
                ...current,
                version: current.version + 1,
                cart: {
                    ...current.cart,
                    version: current.cart.version + 1,
                    lines,
                    totals: {
                        itemCount: lines.reduce((sum, value) => sum + value.line.quantity, 0),
                        subtotal: amount(subtotal),
                        discount: amount(0),
                        shipping: amount(0),
                        tax: amount(0),
                        total: amount(subtotal),
                    },
                },
                updatedAt: new Date(),
            };
            sessions.set(enrollment.id, enrollment);
            cart.replace(
                enrollment.cart.lines.map((value) => value.line),
                enrollment.cart.version,
            );
            return { enrollment };
        },
    );
    transport.register(
        completeCommerceEnrollmentStageOperation,
        ({ enrollmentId, stageId, outcome, expectedVersion }) => {
            const current = requireJourney(sessions, enrollmentId, expectedVersion);
            if (
                current.currentStage.id !== stageId ||
                !current.currentStage.allowedOutcomes.includes(outcome)
            )
                throw failed(completeCommerceEnrollmentStageOperation.name);
            const index = current.stages.findIndex((value) => value.id === stageId);
            const enrollment = {
                ...current,
                version: current.version + 1,
                currentStage: current.stages[Math.min(index + 1, current.stages.length - 1)]!,
                updatedAt: new Date(),
            };
            sessions.set(enrollment.id, enrollment);
            return { enrollment };
        },
    );
    transport.register(returnCommerceEnrollmentToShoppingOperation, () => {
        const current = [...sessions.values()].at(-1);
        if (!current || current.currentStage.kind !== "checkout")
            throw failed(returnCommerceEnrollmentToShoppingOperation.name);
        const target = current.stages.find((value) => value.id === "build-order");
        if (!target) throw failed(returnCommerceEnrollmentToShoppingOperation.name);
        const enrollment = {
            ...current,
            version: current.version + 1,
            status: "active" as const,
            currentStage: target,
            updatedAt: new Date(),
        };
        sessions.set(enrollment.id, enrollment);
        return { enrollment };
    });
    transport.register(abandonCommerceEnrollmentOperation, ({ enrollmentId, expectedVersion }) => {
        requireJourney(sessions, enrollmentId, expectedVersion);
        sessions.delete(enrollmentId);
        cart.replace([]);
        return { abandoned: true };
    });

    return () => [...sessions.values()].at(-1);
}

function defaultStages(): readonly CommerceEnrollmentStage[] {
    return [
        stage("choose-kit", "kit-selection", 1, "Choose your foundation", ["selected"]),
        stage("build-order", "catalogue", 2, "Make it yours", ["continue", "skip"], true),
        stage("checkout", "checkout", 3, "Review and enroll", ["checkout"]),
    ];
}
function stage(
    id: string,
    kind: CommerceEnrollmentStage["kind"],
    current: number,
    title: string,
    outcomes: readonly string[],
    skippable = false,
): CommerceEnrollmentStage {
    return {
        id,
        kind,
        presentation: {
            eyebrow: "Enrollment",
            title,
            description: "A guided, configurable Commerce enrollment stage.",
            primaryActionLabel: "Continue",
            secondaryActionLabel: skippable ? "Not now" : null,
            imageUrl: null,
            accentColor: null,
        },
        progress: { current, total: 3, label: `Step ${current} of 3` },
        allowedOutcomes: outcomes,
        skippable,
        catalogueId: kind === "catalogue" ? "shop" : null,
    };
}
function kit(id: string, name: string, value: number) {
    const money = amount(value);
    const product: CommerceProductSummary = {
        id: `enrollment:${id}`,
        displaySku: id.toUpperCase(),
        slug: id,
        name,
        shortDescription: "A configurable enrollment collection",
        status: "active",
        primaryImage: null,
        representativeOffers: [{ purchaseType: "one-time", price: { current: money } }],
        categoryIds: [],
        tags: ["enrollment"],
    };
    return {
        id,
        product,
        badge: id === "starter" ? "Most popular" : "Best value",
        highlights: ["Guided start", "Secure checkout"],
        available: true,
    };
}
function amount(value: number): CommerceMoney {
    return { amount: value, currencyCode: "NZD", formatted: `$${value.toFixed(2)}` };
}
function emptyCart() {
    const zero = amount(0);
    return {
        id: "enrollment-cart",
        version: 0,
        currencyCode: "NZD",
        lines: [],
        totals: {
            itemCount: 0,
            subtotal: zero,
            discount: zero,
            shipping: zero,
            tax: zero,
            total: zero,
        },
    };
}
function requireJourney(
    sessions: Map<string, CommerceEnrollmentSession>,
    id: string,
    version?: number,
) {
    const value = sessions.get(id);
    if (!value || (version !== undefined && value.version !== version))
        throw failed("commerce.enrollment");
    return value;
}
function failed(operationName: string) {
    return new CommerceTransportError("request-failed", "Enrollment changed or is unavailable.", {
        operationName,
    });
}
